import { FC, PropsWithChildren } from "react";

import { cva, VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { default as Icon, IconType } from "../Icon";

export const colors = {
  rose: ["bg-rose-25", "text-rose-750", "border-rose-750"],
  neutral: ["bg-neutral-12", "border-neutral-425", "text-neutral-425"],
  cadet: ["bg-cadet-25", "text-cadet-850", "border-cadet-850"],
  marigold: ["bg-marigold-25", "text-marigold-750", "border-marigold-750"],
  purple: ["bg-purple-50", "text-purple-725", "border-purple-725"],
  mindaro: ["bg-mindaro-50", "text-mindaro-850", "border-mindaro-850"],
  yellow: ["bg-[#FFF0C4]", "text-[#D8A100]", "border-[#D8A100]"],
  plum: ["bg-[#9800B2]", "bg-opacity-10", "text-[#9800B2]", "border-[#9800B2]"],
  error: ["bg-error-50", "text-error-700", "border-error-700"],
  info: ["bg-info-50", "text-info-700", "border-info-700"],
  indigo: [
    "bg-[#5700C6]",
    "bg-opacity-10",
    "text-[#5700C6]",
    "border-[#5700C6]",
  ],
  navy: ["bg-[#005EB5]", "bg-opacity-10", "text-[#005EB5]", "border-[#005EB5]"],
  seagreen: [
    "bg-[#008C9F]",
    "bg-opacity-10",
    "text-[#008C9F]",
    "border-[#008C9F]",
  ],
  forest: [
    "bg-[#009F5C]",
    "bg-opacity-10",
    "text-[#388555]",
    "border-[#388555]",
  ],
  primary: ["bg-primary-50", "text-primary-800", "border-primary-800"],
  primary_ghost: ["bg-primary-50", "text-primary-800", "border-primary-50"],
  info_ghost: ["bg-info-50", "text-info-700", "border-info-50"],
} as const;

const badge = cva(
  "flex-none inline-flex items-center gap-xs rounded-full w-fit whitespace-nowrap font-normal border border-opacity-20",
  {
    variants: {
      color: colors,
      size: {
        sm: "text-app-caption-1 py-[1px]",
        md: "text-app-caption-1 m:text-app-body-2 py-[2px]",
      },
    },
    defaultVariants: {
      color: "neutral",
    },
  },
);

export interface BadgeProps
  extends PropsWithChildren<VariantProps<typeof badge>> {
  iconStart?: IconType;
  iconEnd?: IconType;
  className?: string;
  smallIcon?: boolean;
}

const Badge: FC<BadgeProps> = ({
  children,
  smallIcon,
  iconStart,
  iconEnd,
  color,
  className,
  size,
}) => {
  return (
    <div
      className={clsx(
        children ? "px-2xs" : "px-xs",
        badge({ color, size }),
        className,
      )}
    >
      {iconStart && (
        <Icon
          className="px-xs"
          size={smallIcon ? "16" : "20"}
          type={iconStart}
          strokeWidth={4}
        />
      )}
      {children}
      {iconEnd && (
        <Icon
          className="mx-xs"
          size={smallIcon ? "16" : "20"}
          type={iconEnd}
          strokeWidth={4}
        />
      )}
    </div>
  );
};

export default Badge;
